import { createContext, memo, useContext, useMemo, type CSSProperties, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { PALETTES, type Palette } from '../thumbs/palettes';
import { CUSTOM_LABEL, thumbHtml, type ThumbSpec } from '../thumbs/thumbHtml';
import s from './Thumb.module.css';

type ThumbContext = { palette: Palette; customLabel: string };

const Ctx = createContext<ThumbContext>({ palette: PALETTES.brand, customLabel: CUSTOM_LABEL });

/**
 * The palette every Thumb below paints with (the site's «Предпочтения для AI» palette): when it
 * changes, every thumbnail re-skins (DESIGN_SPEC §5). `customLabel` is the translated «Свой блок».
 */
export function ThumbPaletteProvider({ palette, customLabel, children }: { palette: Palette; customLabel?: string; children: ReactNode }) {
  const parent = useContext(Ctx);
  const value = useMemo(() => ({ palette, customLabel: customLabel ?? parent.customLabel }), [palette, customLabel, parent.customLabel]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useThumbPalette(): Palette {
  return useContext(Ctx).palette;
}

type Props = ThumbSpec & {
  /** overrides the provider's palette */
  palette?: Palette;
  /** CSS aspect-ratio (the section type's preview or gallery ratio); without it the parent sizes the thumb */
  ratio?: string;
  customLabel?: string;
  className?: string;
  style?: CSSProperties;
};

/**
 * The design's palette-aware wireframe of a section layout (TOK + thumbP): pure CSS in container
 * units, so it renders at any size. Used by the library, the structure outline and gallery, the
 * page preview, the constructor and the AI-mode result. Decorative: the caller labels it.
 */
export const Thumb = memo(function Thumb({ layout, mode, mirrored, palette, ratio, customLabel, className, style }: Props) {
  const ctx = useContext(Ctx);
  const p = palette ?? ctx.palette;
  const label = customLabel ?? ctx.customLabel;
  const html = useMemo(() => thumbHtml({ layout, mode, mirrored }, p, label), [layout, mode, mirrored, p, label]);
  return (
    <div className={cx(s.thumb, ratio && s.sized, className)} style={ratio ? { aspectRatio: ratio, ...style } : style} aria-hidden data-thumb={`${layout}:${mode ?? 'light'}:${mirrored ? 1 : 0}`}>
      {/* nosemgrep: studio-no-dangerously-set-inner-html -- thumbHtml: verbatim templates, validated hex colours, an escaped caption */}
      <div className={s.fill} dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
});
